import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Alert, IconButton, InputAdornment } from "@mui/material";
import AuthPrimaryButton from "./AuthPrimaryButton";
import AuthTextField from "./AuthTextField";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  passwordSchema,
  type PasswordSchemaType,
} from "../../domain/validation/passwordSchema";
import {
  AUTH_FLOW_PURPOSE_KEY,
  AUTH_FLOW_PURPOSE_LOGIN,
  AUTH_FLOW_PURPOSE_PASSWORD_RESET,
} from "../../domain/constants/authFlowConstants";
import { usePassword } from "../../application/hooks/usePassword";
import { useSession } from "../../application/hooks/useSession";
import { useNavigate } from "react-router-dom";

interface SetupPasswordProps {
  email?: string;
  purpose?: string;
}

const passwordFieldSx = {
  "& input::-ms-reveal": { display: "none" },
  "& input::-ms-clear": { display: "none" },
  "& input::-webkit-password-toggle-button": { display: "none" },
};

const SetupPassword = ({ purpose: purposeProp }: SetupPasswordProps) => {
  const { t } = useTranslation("auth");
  const { setPassword, resetPassword, loading, error } = usePassword();
  const { getPasswordResetToken } = useSession();
  const navigate = useNavigate();

  const purpose =
    purposeProp ||
    sessionStorage.getItem(AUTH_FLOW_PURPOSE_KEY) ||
    AUTH_FLOW_PURPOSE_LOGIN;

  const [passwordForm, setPasswordForm] = React.useState<PasswordSchemaType>({
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = React.useState<Partial<PasswordSchemaType>>({});
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
    setSubmitError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const result = passwordSchema.safeParse(passwordForm);

    if (!result.success) {
      const fieldErrors: Partial<PasswordSchemaType> = {};
      result.error.issues.forEach((err) => {
        const field = err.path[0] as keyof PasswordSchemaType;
        fieldErrors[field] = t(err.message);
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    try {
      if (purpose === AUTH_FLOW_PURPOSE_PASSWORD_RESET) {
        const resetToken = getPasswordResetToken();
        if (!resetToken) {
          setSubmitError(t("setupPassword.sessionExpired"));
          return;
        }

        await resetPassword(result.data, resetToken);
        sessionStorage.removeItem(AUTH_FLOW_PURPOSE_KEY);
        navigate("/forgot-password-success");
      } else {
        await setPassword(result.data);
        sessionStorage.removeItem(AUTH_FLOW_PURPOSE_KEY);
        navigate("/setup-twofa");
      }
    } catch (err: unknown) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : error ?? t("setupPassword.genericError"),
      );
    }
  };

  const displayError = submitError ?? error;

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6">
      <AuthTextField
        name="password"
        label={t("setupPassword.passwordLabel")}
        type={showPassword ? "text" : "password"}
        placeholder="*******"
        value={passwordForm.password}
        onChange={handleChange}
        error={!!errors.password}
        helperText={errors.password}
        disabled={loading}
        fullWidth
        sx={passwordFieldSx}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={
                  showPassword ? t("login.showPassword") : t("login.hidePassword")
                }
                onClick={handleClickShowPassword}
                edge="end"
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <AuthTextField
        name="confirmPassword"
        label={t("setupPassword.confirmPasswordLabel")}
        type={showConfirmPassword ? "text" : "password"}
        placeholder="*******"
        value={passwordForm.confirmPassword}
        onChange={handleChange}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword}
        disabled={loading}
        fullWidth
        sx={passwordFieldSx}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={
                  showConfirmPassword
                    ? t("login.showPassword")
                    : t("login.hidePassword")
                }
                onClick={handleClickShowConfirmPassword}
                edge="end"
              >
                {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {displayError && (
        <Alert severity="error" sx={{ py: 0 }}>
          {displayError}
        </Alert>
      )}

      <AuthPrimaryButton type="submit" loading={loading}>
        {t("setupPassword.submit")}
      </AuthPrimaryButton>
    </form>
  );
};

export default SetupPassword;
